/**
 * The app sidebar's size on a desktop screen, ported from Bilanss (lib/layout/sidebarSize.ts): a width the person
 * drags between a floor and a ceiling, or the icon rail. Below 768px (Tailwind's md, where x11's phone header and
 * drawer take over) none of this applies.
 *
 * The size is a per-browser convenience: it lives in local storage and never reaches the server.
 */

/** The rail: the logo, the fold key and the navigation icons in one column (12 + 40 + 12). */
export const RAIL_WIDTH = 64

/** The floor, which is also the default: narrower than this the sidebar folds to the rail rather than squeezing. */
export const MIN_WIDTH = 224
export const MIN_WIDTH_COMPACT = 200

/** Wider than this the sidebar stops being a list and becomes a second page. */
export const MAX_WIDTH = 320

/** However wide the sidebar is dragged, the page beside it keeps at least this much room. */
export const MIN_CONTENT_WIDTH = 560

/** Arrow keys move the edge one small step of the spacing scale; with Shift, one large step. */
export const KEY_STEP = 8
export const KEY_STEP_LARGE = 40

/** Every width is a whole step of the 4px grid. */
const GRID = 4

/** Above this viewport width the sidebar has its wide gutters and default; up to it, the compact ones. */
const WIDE_VIEWPORT = 1200

/** Below this viewport width the phone header and drawer replace the sidebar (Tailwind's md breakpoint). */
export const DRAWER_QUERY = '(max-width: 767px)'

export interface SidebarLayout {
  /** Whether the sidebar is folded to the icon rail. */
  collapsed: boolean
  /** The width the person chose, kept while collapsed so unfolding returns to it; null is the screen's default. */
  width: number | null
}

export const DEFAULT_SIDEBAR_LAYOUT: SidebarLayout = { collapsed: false, width: null }

function toGrid(width: number): number {
  return Math.round(width / GRID) * GRID
}

function isWide(viewportWidth: number): boolean {
  return viewportWidth > WIDE_VIEWPORT
}

/** The narrowest the full sidebar may be on this screen. */
export function minSidebarWidth(viewportWidth: number): number {
  return isWide(viewportWidth) ? MIN_WIDTH : MIN_WIDTH_COMPACT
}

/** The width the stylesheet gives a sidebar nobody has resized (styles/sidebar.css). */
export function defaultSidebarWidth(viewportWidth: number): number {
  return minSidebarWidth(viewportWidth)
}

/** The widest the sidebar may be on this screen: the ceiling, or less when the page would otherwise get cramped. */
export function maxSidebarWidth(viewportWidth: number): number {
  const leavingRoomForContent = Math.floor((viewportWidth - MIN_CONTENT_WIDTH) / GRID) * GRID
  return Math.max(minSidebarWidth(viewportWidth), Math.min(MAX_WIDTH, leavingRoomForContent))
}

/** A chosen width, on the grid and between this screen's floor and ceiling. */
export function clampSidebarWidth(width: number, viewportWidth: number): number {
  const floor = minSidebarWidth(viewportWidth)
  return Math.min(maxSidebarWidth(viewportWidth), Math.max(floor, toGrid(width)))
}

/** The width the sidebar actually takes: the rail, the chosen width fitted to the screen, or the default. */
export function sidebarWidth(layout: SidebarLayout, viewportWidth: number): number {
  if (layout.collapsed) return RAIL_WIDTH
  if (layout.width === null) return defaultSidebarWidth(viewportWidth)
  return clampSidebarWidth(layout.width, viewportWidth)
}

/** The fold key and the shortcut fold and unfold the sidebar; the chosen width is kept for unfolding. */
export function toggleSidebar(layout: SidebarLayout): SidebarLayout {
  return { ...layout, collapsed: !layout.collapsed }
}

/**
 * A drag that ends below the midpoint between the rail and the floor folds the sidebar; above it, the sidebar
 * holds at the floor rather than squeezing its labels.
 */
export function railSnapWidth(viewportWidth: number): number {
  return (RAIL_WIDTH + minSidebarWidth(viewportWidth)) / 2
}

/**
 * Where a drag of the edge lands. Past the snap point the sidebar folds to the rail and remembers the width it had
 * before the drag; anywhere else it takes the dragged width, fitted to the screen.
 */
export function layoutFromDrag(
  draggedWidth: number,
  viewportWidth: number,
  widthBeforeDrag: number | null,
): SidebarLayout {
  if (draggedWidth < railSnapWidth(viewportWidth)) return { collapsed: true, width: widthBeforeDrag }
  return { collapsed: false, width: clampSidebarWidth(draggedWidth, viewportWidth) }
}

/**
 * The keys of the resize edge, as the ARIA window splitter pattern describes them: the arrows step, Home and End
 * go to the floor and the ceiling, Enter folds and unfolds. Null for any other key.
 */
export function layoutFromKey(
  layout: SidebarLayout,
  key: string,
  shiftKey: boolean,
  viewportWidth: number,
): SidebarLayout | null {
  const current = sidebarWidth(layout, viewportWidth)
  const floor = minSidebarWidth(viewportWidth)
  const step = shiftKey ? KEY_STEP_LARGE : KEY_STEP

  switch (key) {
    case 'ArrowLeft':
      if (layout.collapsed) return null
      // At the floor, one more step folds the sidebar, as a drag past the floor does.
      if (current <= floor) return { collapsed: true, width: layout.width }
      return { collapsed: false, width: clampSidebarWidth(current - step, viewportWidth) }
    case 'ArrowRight':
      // From the rail, the first step right unfolds the sidebar to the chosen width, as Enter does.
      if (layout.collapsed) return toggleSidebar(layout)
      return { collapsed: false, width: clampSidebarWidth(current + step, viewportWidth) }
    case 'Home':
      return { collapsed: false, width: floor }
    case 'End':
      return { collapsed: false, width: maxSidebarWidth(viewportWidth) }
    case 'Enter':
      return toggleSidebar(layout)
    default:
      return null
  }
}

/** Anything unreadable in storage is the default layout; a stored width is trusted only after clamping. */
export function parseSidebarLayout(stored: string | null): SidebarLayout {
  if (!stored) return DEFAULT_SIDEBAR_LAYOUT
  let value: unknown
  try {
    value = JSON.parse(stored)
  } catch {
    return DEFAULT_SIDEBAR_LAYOUT
  }
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return DEFAULT_SIDEBAR_LAYOUT

  const { collapsed, width } = value as Record<string, unknown>
  const storedWidth =
    typeof width === 'number' && Number.isFinite(width)
      ? Math.min(MAX_WIDTH, Math.max(MIN_WIDTH_COMPACT, toGrid(width)))
      : null
  return { collapsed: collapsed === true, width: storedWidth }
}

export function serializeSidebarLayout(layout: SidebarLayout): string {
  return JSON.stringify({ collapsed: layout.collapsed, width: layout.width })
}

/** The keys of a keyboard event the shortcut reads. */
export interface ShortcutKeys {
  key: string
  code: string
  metaKey: boolean
  ctrlKey: boolean
  altKey: boolean
  shiftKey: boolean
  repeat?: boolean
}

/**
 * ⌘\ on a Mac, Ctrl+\ elsewhere: no browser or screen reader binds it, and it needs a modifier, so typing never
 * triggers it. The physical key is matched too, for layouts where the key beside Enter is not "\".
 */
export function isSidebarShortcut(keys: ShortcutKeys, isMac: boolean): boolean {
  const command = isMac ? keys.metaKey && !keys.ctrlKey : keys.ctrlKey && !keys.metaKey
  const backslash = keys.code === 'Backslash' || keys.key === '\\'
  return command && backslash && !keys.altKey && !keys.shiftKey && !keys.repeat
}

/** How the shortcut is written for the platform, in a tooltip and in aria-keyshortcuts. */
export function sidebarShortcutLabel(isMac: boolean): { shown: string; aria: string } {
  return isMac ? { shown: '⌘\\', aria: 'Meta+\\' } : { shown: 'Ctrl+\\', aria: 'Control+\\' }
}

/** While focus is in a field the shortcut belongs to the field. */
export function isTypingTarget(element: { tagName: string; isContentEditable?: boolean } | null): boolean {
  if (!element) return false
  if (element.isContentEditable) return true
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName.toUpperCase())
}

/**
 * Where a sidebar tooltip stands, in viewport pixels (Bilanss lib/layout/sidebarTooltip.ts). Beside: past the
 * sidebar's edge, level with its element, which is how the rail names its icons. Below: just under its element and
 * ending flush with its right edge, so it hangs from the element inside the sidebar (the full sidebar's fold key).
 */
export type TipPlace = 'beside' | 'below'

/** The gap between a tooltip and what it names: two steps of the spacing scale. */
export const TIP_GAP = 8

export function sidebarTipPosition(
  anchor: { top: number; bottom: number; right: number },
  sidebarRight: number,
  place: TipPlace,
): { top: number; left: number } {
  if (place === 'below') return { top: anchor.bottom + TIP_GAP, left: anchor.right }
  return { top: (anchor.top + anchor.bottom) / 2, left: sidebarRight + TIP_GAP }
}
